import {EntryNote} from 'notebook/util/NoteUtilConstants';

/**
 * A hierarchical 2-part index of a note part within an entry day within a document.
 */
export interface NotePartIndex {
  entryDayIndex: number;
  notePartLocation: number;
}

/**
 * Given a "global" entry index, return a hierarchical NotePartIndex.
 */
export function getNotePartIndexFromEntryIndex(entryIndex: number, entryNote: EntryNote): NotePartIndex {
  _assertValidIndex(entryIndex, entryNote.noteParts);

  const entryDayIndex = entryNote.days.findIndex((entryDay, entryDayIndex, entryDays) => {
    const nextDayStartIndex = entryDays[entryDayIndex + 1]?.index;
    // Either we reached the last entryDay (nextDayStartIndex is undefined) or the given index is between
    // the start of the current entryDay and the start of the next entryDay.
    return (
      !nextDayStartIndex ||
      (entryIndex >= entryDay.index && nextDayStartIndex && entryIndex < nextDayStartIndex)
    );
  });
  const entryDay = entryNote.days[entryDayIndex];
  return {
    entryDayIndex,
    notePartLocation: entryIndex - entryDay.index,
  };
}

/**
 * Given a hierarchical NotePartIndex return a single "global" entry index.
 */
export function getEntryIndexFromNotePartIndex(notePartIndex: NotePartIndex, entryNote: EntryNote): number {
  _assertValidIndex(notePartIndex.entryDayIndex, entryNote.days);
  const entryIndex = entryNote.days[notePartIndex.entryDayIndex].index + notePartIndex.notePartLocation;
  _assertValidIndex(entryIndex, entryNote.noteParts);
  return entryIndex;
}

/**
 * Throw if the given index is < 0 or exceeds the length of the array.
 */
function _assertValidIndex(index: number, array: {length: number}): void {
  if (!(index >= 0 && index < array.length)) {
    throw new Error(`Bad index: ${index}. Expected 0 <= index < ${array.length}`);
  }
}
